/**
 * OrgSwitcher — dropdown en el header del dashboard cuando el usuario
 * pertenece a más de una empresa. Si solo tiene una (o cero), no se
 * renderiza nada.
 *
 *   ┌────────────────────────────────────┐
 *   │ 🏢 AMERITIC SAS · OWNER       ▾  │  ← trigger
 *   └────────────────────────────────────┘
 *           ┌───────────────────────────────┐
 *           │ AMERITIC SAS         OWNER  ✓ │
 *           │ NIT 900978942                 │
 *           ├───────────────────────────────┤
 *           │ SERVILINK COLOMBIA   OWNER    │
 *           │ NIT 901785899                 │
 *           └───────────────────────────────┘
 */

import { useEffect, useRef, useState } from 'react'
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import type { MembershipInfo } from '@/lib/api'
import { useCurrentUser } from '@/hooks/api/use-current-user'
import { useSwitchOrg } from '@/hooks/use-switch-org'

export function OrgSwitcher() {
  const { data: me } = useCurrentUser()
  const switchOrg = useSwitchOrg()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Outside-click + Escape close.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!me) return null

  const memberships = me.memberships ?? []
  // Si no tiene empresas, no mostramos nada (es persona natural sin org).
  // Si solo tiene una, mostramos el chip estático sin dropdown.
  if (memberships.length === 0) return null

  if (memberships.length === 1) {
    const only = memberships[0]
    if (!only.organization) return null
    return (
      <div className="hidden items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-xs sm:inline-flex">
        <Building2 className="h-3.5 w-3.5 text-fg-muted" />
        <span className="max-w-[160px] truncate font-medium text-fg-primary">
          {only.organization.razon_social}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
          {only.role}
        </span>
      </div>
    )
  }

  // 2+ orgs: dropdown con switch.
  const active = memberships.find((m) => m.is_active_session_org) ?? memberships[0]
  const others = memberships.filter((m) => m !== active)

  const onPick = async (organization_id: string, label: string) => {
    if (busy) return
    setBusy(organization_id)
    try {
      await switchOrg(organization_id)
      toast.success(`Cambiaste a ${label}`)
      setOpen(false)
      // Forzar refresh para que el header muestre la nueva org.
      // (qc.clear() ya invalidó las queries; esto re-hidrata el SPA).
      window.location.reload()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos cambiar de empresa')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-xs transition-colors hover:bg-bg-surface"
      >
        <Building2 className="h-3.5 w-3.5 text-fg-muted" />
        <span className="max-w-[180px] truncate font-medium text-fg-primary">
          {active.organization?.razon_social ?? 'Sin empresa'}
        </span>
        <span className="hidden font-mono text-[10px] uppercase tracking-wider text-fg-muted lg:inline">
          {active.role}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-border bg-bg-surface py-1 shadow-xl"
        >
          {/* active row, marcada */}
          <div className="border-b border-border px-3 py-1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
              Empresa actual
            </p>
            <OrgRow membership={active} active />
          </div>

          {/* otras */}
          {others.length > 0 && (
            <>
              <div className="px-3 pt-2">
                <p className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
                  Cambiar a
                </p>
              </div>
              <ul className="py-1">
                {others.map((m) => (
                  <li key={m.membership_id}>
                    <button
                      type="button"
                      onClick={() =>
                        m.organization &&
                        onPick(m.organization_id, m.organization.razon_social)
                      }
                      disabled={busy !== null}
                      className="block w-full text-left transition-colors hover:bg-bg-elevated disabled:opacity-50"
                    >
                      <OrgRow
                        membership={m}
                        active={false}
                        loading={busy === m.organization_id}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function OrgRow({
  membership,
  active,
  loading,
}: {
  membership: MembershipInfo
  active: boolean
  loading?: boolean
}) {
  const org = membership.organization
  if (!org) return null
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg-primary">{org.razon_social}</p>
        <p className="text-xs text-fg-muted">
          {org.entity_type === 'company' ? 'NIT' : 'Doc'} {org.tax_id} ·{' '}
          <span className="font-mono uppercase tracking-wider">{membership.role}</span>
        </p>
      </div>
      {loading ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-fg-muted" />
      ) : active ? (
        <Check className="h-4 w-4 shrink-0 text-success-fg" />
      ) : null}
    </div>
  )
}
